import { useQuery } from '@tanstack/react-query'
import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { api, SemRede, type LinhaPainel, type SituacaoPlano } from '../dados/api.js'
import { PainelAdmin, SemRedeAqui } from './admin-comum.js'

/// Painel das inspeções, notificações e interdições — o mesmo recorte do BI
/// "Notificações e Interdições" do SSMA, dentro do sistema.
///
/// Como no Power BI, **cada barra é também um filtro**: tocar numa obra filtra todos
/// os outros gráficos por ela, e o gráfico da obra continua mostrando as demais,
/// com a escolhida em destaque. Os números saem das mesmas linhas que o servidor
/// entrega, filtradas e somadas aqui.
///
/// Só com rede: o painel lê o banco do servidor, que tem todas as inspeções das
/// obras da pessoa — o aparelho pode ter só parte delas.

type Dimensao =
  | 'mes'
  | 'tipo'
  | 'criticidade'
  | 'criticidadeApia'
  | 'plano'
  | 'empresa'
  | 'tipologia'
  | 'obra'
  | 'tecnico'
  | 'gerente'
  | 'encarregado'

type Selecao = Partial<Record<Dimensao, string>>

type Periodo = '12m' | 'ano' | 'anoPassado' | 'tudo'

const NAO_INFORMADO = '—'

const ROTULO_DIMENSAO: Record<Dimensao, string> = {
  mes: 'Mês',
  tipo: 'Tipo',
  criticidade: 'Criticidade',
  criticidadeApia: 'Criticidade Ápia',
  plano: 'Plano de ação',
  empresa: 'Empresa emitente',
  tipologia: 'Tipologia',
  obra: 'Obra',
  tecnico: 'Técnico de segurança',
  gerente: 'Gerente de contrato',
  encarregado: 'Encarregado',
}

const ORDEM_TIPO = ['Inspeção', 'Notificação', 'Interdição', 'Inspeção Direcionada']
const ORDEM_CRITICIDADE = ['Leve', 'Moderada', 'Alta']
const ORDEM_CRITICIDADE_APIA = ['Baixa', 'Média', 'Alta', 'Sem revisão']
const ORDEM_PLANO: SituacaoPlano[] = ['Concluído', 'Concluído com atraso', 'Atrasado', 'Em andamento', 'Sem prazo']

const ROTULO_CRITICIDADE_APIA: Record<string, string> = { BAIXA: 'Baixa', MEDIA: 'Média', ALTA: 'Alta' }

/// Cores de estado: só onde a cor quer dizer bom/ruim, e sempre com o nome escrito
/// ao lado. As demais barras são de uma série só, e vão todas no azul da Ápia.
const COR_ESTADO: Record<string, string> = {
  Leve: 'bg-leve',
  Moderada: 'bg-moderada',
  Alta: 'bg-alta',
  Baixa: 'bg-leve',
  Média: 'bg-moderada',
  'Sem revisão': 'bg-slate-300',
  Concluído: 'bg-leve',
  'Concluído com atraso': 'bg-moderada',
  Atrasado: 'bg-alta',
  'Em andamento': 'bg-marinho',
  'Sem prazo': 'bg-slate-300',
}

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']

/// Nomes digitados à mão: "JOÃO DA SILVA", "João da Silva " e "joao da silva" são a
/// mesma pessoa. A chave junta; o rótulo é a grafia mais usada.
function chaveNome(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLocaleUpperCase('pt-BR')
}

/// Os valores de uma linha numa dimensão. Empresa emitente pode ter os dois.
function valores(l: LinhaPainel, d: Dimensao, obras: Map<string, string>): string[] {
  switch (d) {
    case 'mes':
      return [l.data ? l.data.slice(0, 7) : NAO_INFORMADO]
    case 'tipo':
      return [l.tipo ?? NAO_INFORMADO]
    case 'criticidade':
      return [l.criticidade ?? NAO_INFORMADO]
    case 'criticidadeApia':
      return [l.criticidadeApia ? (ROTULO_CRITICIDADE_APIA[l.criticidadeApia] ?? l.criticidadeApia) : 'Sem revisão']
    case 'plano':
      return [l.plano]
    case 'empresa':
      return l.empresa.length > 0 ? l.empresa : [NAO_INFORMADO]
    case 'tipologia':
      return [l.tipologia ?? NAO_INFORMADO]
    case 'obra':
      return [obras.get(l.obraId) ?? NAO_INFORMADO]
    case 'tecnico':
    case 'gerente':
    case 'encarregado': {
      const nome = l[d]
      return [nome && nome.trim() ? chaveNome(nome) : NAO_INFORMADO]
    }
  }
}

function dentroDoPeriodo(data: string | null, periodo: Periodo, hoje: string): boolean {
  if (periodo === 'tudo') return true
  if (!data) return false
  const ano = Number(hoje.slice(0, 4))
  if (periodo === 'ano') return data.startsWith(String(ano))
  if (periodo === 'anoPassado') return data.startsWith(String(ano - 1))
  // Últimos 12 meses: do primeiro dia do mês de 11 meses atrás até hoje.
  const meses = ano * 12 + (Number(hoje.slice(5, 7)) - 1) - 11
  const inicio = `${Math.floor(meses / 12)}-${String((meses % 12) + 1).padStart(2, '0')}-01`
  return data >= inicio && data <= hoje
}

function atende(l: LinhaPainel, selecao: Selecao, obras: Map<string, string>, exceto?: Dimensao): boolean {
  for (const [d, v] of Object.entries(selecao) as [Dimensao, string][]) {
    if (d === exceto || v === undefined) continue
    if (!valores(l, d, obras).includes(v)) return false
  }
  return true
}

interface Barra {
  chave: string
  rotulo: string
  valor: number
}

/// Contagem por dimensão, sobre as linhas que passam por todos os filtros menos o
/// da própria dimensão — é o que deixa as outras barras visíveis ao escolher uma.
function contar(
  linhas: LinhaPainel[],
  d: Dimensao,
  selecao: Selecao,
  obras: Map<string, string>,
): Barra[] {
  const contagem = new Map<string, number>()
  const grafias = new Map<string, Map<string, number>>()
  const ehNome = d === 'tecnico' || d === 'gerente' || d === 'encarregado'
  for (const l of linhas) {
    if (!atende(l, selecao, obras, d)) continue
    for (const v of valores(l, d, obras)) {
      contagem.set(v, (contagem.get(v) ?? 0) + 1)
      if (ehNome && v !== NAO_INFORMADO) {
        const original = (l[d as 'tecnico'] ?? '').replace(/\s+/g, ' ').trim()
        const porGrafia = grafias.get(v) ?? new Map<string, number>()
        porGrafia.set(original, (porGrafia.get(original) ?? 0) + 1)
        grafias.set(v, porGrafia)
      }
    }
  }
  return [...contagem].map(([chave, valor]) => {
    const g = grafias.get(chave)
    const rotulo = g ? [...g].sort((a, b) => b[1] - a[1])[0]![0] : chave === NAO_INFORMADO ? 'Não informado' : chave
    return { chave, rotulo, valor }
  })
}

function ordenar(barras: Barra[], ordem?: readonly string[]): Barra[] {
  if (ordem) {
    const pos = (c: string) => {
      const i = ordem.indexOf(c)
      return i === -1 ? ordem.length : i
    }
    return [...barras].sort((a, b) => pos(a.chave) - pos(b.chave) || b.valor - a.valor)
  }
  // "Não informado" sempre por último: não é uma categoria, é falta de dado.
  return [...barras].sort(
    (a, b) =>
      Number(a.chave === NAO_INFORMADO) - Number(b.chave === NAO_INFORMADO) ||
      b.valor - a.valor ||
      a.rotulo.localeCompare(b.rotulo, 'pt-BR'),
  )
}

const pct = (parte: number, todo: number) =>
  todo > 0 ? `${((parte / todo) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%` : '—'

export function Painel() {
  const consulta = useQuery({
    queryKey: ['painel'],
    queryFn: () => api.painel(),
    staleTime: 5 * 60_000,
    placeholderData: (anterior) => anterior,
  })
  const [periodo, setPeriodo] = useState<Periodo>('12m')
  const [selecao, setSelecao] = useState<Selecao>({})

  const dados = consulta.data
  const obras = useMemo(
    () => new Map((dados?.obras ?? []).map((o) => [o.id, `${o.codigo} · ${o.nome}`])),
    [dados?.obras],
  )
  const doPeriodo = useMemo(
    () => (dados ? dados.linhas.filter((l) => dentroDoPeriodo(l.data, periodo, dados.hoje)) : []),
    [dados, periodo],
  )
  const filtradas = useMemo(() => doPeriodo.filter((l) => atende(l, selecao, obras)), [doPeriodo, selecao, obras])

  function alternar(d: Dimensao, chave: string) {
    setSelecao((atual) => {
      const nova = { ...atual }
      if (nova[d] === chave) delete nova[d]
      else nova[d] = chave
      return nova
    })
  }

  // Sem rede a consulta fica *pausada*, e não com erro: o React Query espera a rede
  // voltar para tentar de novo. Sem olhar para isso, a tela ficava em "Carregando"
  // até lá.
  if (!dados && (consulta.isError || consulta.fetchStatus === 'paused')) {
    return (
      <PainelAdmin titulo="Painel" subtitulo="Inspeções, notificações e interdições">
        <SemRedeAqui erro={consulta.error ?? consulta.failureReason ?? new SemRede()} />
      </PainelAdmin>
    )
  }
  if (!dados) return <p className="py-10 text-center text-slate-500">Carregando o painel…</p>

  const grafico = (d: Dimensao, titulo: string, opcoes: { ordem?: readonly string[]; limite?: number } = {}) => (
    <GraficoBarras
      titulo={titulo}
      barras={ordenar(contar(doPeriodo, d, selecao, obras), opcoes.ordem)}
      selecionada={selecao[d]}
      estado={opcoes.ordem !== undefined && d !== 'tipo'}
      limite={opcoes.limite}
      onEscolher={(chave) => alternar(d, chave)}
    />
  )

  const total = filtradas.length
  const porTipo = (t: string) => filtradas.filter((l) => l.tipo === t).length
  const concluidos = filtradas.filter((l) => l.plano === 'Concluído').length
  const comAtraso = filtradas.filter((l) => l.plano === 'Concluído com atraso').length
  const atrasados = filtradas.filter((l) => l.plano === 'Atrasado').length
  const baseAderencia = concluidos + comAtraso + atrasados
  const ativos = Object.entries(selecao) as [Dimensao, string][]

  return (
    <PainelAdmin titulo="Painel" subtitulo="Inspeções, notificações e interdições (checklist INI)">
      {/* Uma faixa de filtros só, acima de tudo: todos os gráficos respondem a ela. */}
      <div className="cartao mb-3 flex flex-wrap items-end gap-2 p-3">
        <Filtro rotulo="Período">
          <select value={periodo} onChange={(e) => setPeriodo(e.target.value as Periodo)} className="campo !py-1.5">
            <option value="12m">12 meses</option>
            <option value="ano">Este ano</option>
            <option value="anoPassado">Ano passado</option>
            <option value="tudo">Tudo</option>
          </select>
        </Filtro>
        <FiltroLista
          rotulo="Obra"
          valor={selecao.obra}
          opcoes={[...new Set(dados.obras.map((o) => obras.get(o.id)!))]}
          onMudar={(v) => mudarSelecao(setSelecao, 'obra', v)}
        />
        <FiltroLista
          rotulo="Tipo"
          valor={selecao.tipo}
          opcoes={ORDEM_TIPO}
          onMudar={(v) => mudarSelecao(setSelecao, 'tipo', v)}
        />
        <FiltroLista
          rotulo="Criticidade Ápia"
          valor={selecao.criticidadeApia}
          opcoes={ORDEM_CRITICIDADE_APIA}
          onMudar={(v) => mudarSelecao(setSelecao, 'criticidadeApia', v)}
        />
        <FiltroLista
          rotulo="Plano de ação"
          valor={selecao.plano}
          opcoes={ORDEM_PLANO}
          onMudar={(v) => mudarSelecao(setSelecao, 'plano', v)}
        />
      </div>

      {ativos.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5 text-xs">
          <span className="text-slate-500">Filtrando por:</span>
          {ativos.map(([d, v]) => (
            <button
              key={d}
              onClick={() => alternar(d, v)}
              className="etiqueta bg-marinho-50 text-marinho hover:bg-marinho-100"
              aria-label={`Tirar o filtro ${ROTULO_DIMENSAO[d]}`}
            >
              {ROTULO_DIMENSAO[d]}: {rotuloDoValor(d, v, doPeriodo)} ✕
            </button>
          ))}
          <button onClick={() => setSelecao({})} className="font-semibold text-marinho underline">
            Limpar filtros
          </button>
        </div>
      )}

      <div className={`space-y-3 transition-opacity ${consulta.isFetching ? 'opacity-60' : ''}`}>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <div className="cartao col-span-2 p-4 sm:col-span-1">
            <p className="text-xs text-slate-500">Registros</p>
            <p className="text-5xl font-bold text-marinho">{total.toLocaleString('pt-BR')}</p>
          </div>
          <Indicador rotulo="Interdições" valor={porTipo('Interdição').toLocaleString('pt-BR')} />
          <Indicador rotulo="Notificações" valor={porTipo('Notificação').toLocaleString('pt-BR')} />
          <Indicador
            largo
            rotulo="Aderência dos planos"
            valor={pct(concluidos, baseAderencia)}
            detalhe={`${concluidos.toLocaleString('pt-BR')} no prazo de ${baseAderencia.toLocaleString('pt-BR')} com prazo vencido ou concluídos`}
          />
        </div>

        <GraficoMeses
          linhas={doPeriodo}
          selecao={selecao}
          obras={obras}
          hoje={dados.hoje}
          selecionado={selecao.mes}
          onEscolher={(m) => alternar('mes', m)}
        />

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {grafico('tipo', 'Por tipo', { ordem: ORDEM_TIPO })}
          {grafico('plano', 'Situação do plano de ação', { ordem: ORDEM_PLANO })}
          {grafico('criticidade', 'Criticidade (inspetor)', { ordem: ORDEM_CRITICIDADE })}
          {grafico('criticidadeApia', 'Criticidade Ápia (SESMT)', { ordem: ORDEM_CRITICIDADE_APIA })}
          {grafico('empresa', 'Empresa emitente')}
          {grafico('obra', 'Por obra', { limite: 10 })}
          {grafico('tipologia', 'Tipologia', { limite: 10 })}
          {grafico('gerente', 'Gerente de contrato', { limite: 10 })}
          {grafico('tecnico', 'Técnico de segurança', { limite: 10 })}
          {grafico('encarregado', 'Encarregado', { limite: 10 })}
        </div>

        <TabelaRegistros linhas={filtradas} obras={obras} />

        <p className="text-center text-xs text-slate-500">
          Aderência = planos concluídos no prazo ÷ (concluídos no prazo + concluídos com atraso + atrasados), como
          no BI. Datas pelo campo "Data Criacao". Dados de{' '}
          {new Date(consulta.dataUpdatedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          {' · '}
          <button onClick={() => void consulta.refetch()} className="font-semibold text-marinho underline">
            atualizar
          </button>
        </p>
      </div>
    </PainelAdmin>
  )
}

function mudarSelecao(setSelecao: (f: (s: Selecao) => Selecao) => void, d: Dimensao, v: string) {
  setSelecao((atual) => {
    const nova = { ...atual }
    if (v) nova[d] = v
    else delete nova[d]
    return nova
  })
}

/// O rótulo de um valor escolhido — para nomes, a grafia mais comum, e não a chave.
function rotuloDoValor(d: Dimensao, v: string, linhas: LinhaPainel[]): string {
  if (v === NAO_INFORMADO) return 'Não informado'
  if (d === 'mes') return rotuloMes(v)
  if (d === 'tecnico' || d === 'gerente' || d === 'encarregado') {
    // A grafia mais usada, a mesma que a barra mostra.
    const grafias = new Map<string, number>()
    for (const x of linhas) {
      const nome = x[d]
      if (!nome || chaveNome(nome) !== v) continue
      const g = nome.replace(/\s+/g, ' ').trim()
      grafias.set(g, (grafias.get(g) ?? 0) + 1)
    }
    return [...grafias].sort((a, b) => b[1] - a[1])[0]?.[0] ?? v
  }
  return v
}

function rotuloMes(anoMes: string): string {
  if (anoMes === NAO_INFORMADO) return 'Sem data'
  const [a, m] = anoMes.split('-')
  return `${MESES[Number(m) - 1]}/${a!.slice(2)}`
}

function Filtro({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <label className="min-w-[9rem] flex-1">
      <span className="mb-0.5 block text-[11px] font-semibold text-slate-500">{rotulo}</span>
      {children}
    </label>
  )
}

function FiltroLista({
  rotulo,
  valor,
  opcoes,
  onMudar,
}: {
  rotulo: string
  valor: string | undefined
  opcoes: readonly string[]
  onMudar: (v: string) => void
}) {
  // Escolhido numa barra e fora da lista (ex.: "Não informado"): entra como opção.
  // Sem isso o select mostraria "Todos" com o painel filtrado, e escolher "Todos"
  // não faria nada, porque já estaria marcado.
  const fora = valor !== undefined && !opcoes.includes(valor)
  return (
    <Filtro rotulo={rotulo}>
      <select value={valor ?? ''} onChange={(e) => onMudar(e.target.value)} className="campo !py-1.5">
        <option value="">Todos</option>
        {opcoes.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
        {fora && <option value={valor}>{valor === NAO_INFORMADO ? 'Não informado' : valor}</option>}
      </select>
    </Filtro>
  )
}

function Indicador({
  rotulo,
  valor,
  detalhe,
  largo,
}: {
  rotulo: string
  valor: string
  detalhe?: string
  /// Ocupa a linha inteira no celular, onde a grade tem duas colunas.
  largo?: boolean
}) {
  return (
    <div className={`cartao p-4 ${largo ? 'col-span-2 sm:col-span-1' : ''}`}>
      <p className="text-xs text-slate-500">{rotulo}</p>
      <p className="text-2xl font-semibold text-slate-900">{valor}</p>
      {detalhe && <p className="mt-0.5 text-[11px] leading-tight text-slate-500">{detalhe}</p>}
    </div>
  )
}

/// Barras horizontais: rótulo, barra e valor na ponta. Cada linha é um botão que
/// filtra o painel inteiro; o nome e o número estão escritos, então a cor nunca é
/// a única forma de ler.
function GraficoBarras({
  titulo,
  barras,
  selecionada,
  estado,
  limite,
  onEscolher,
}: {
  titulo: string
  barras: Barra[]
  selecionada: string | undefined
  /// Cores de estado (verde/âmbar/vermelho) em vez do azul único.
  estado: boolean
  limite?: number
  onEscolher: (chave: string) => void
}) {
  const [todas, setTodas] = useState(false)
  const soma = barras.reduce((s, b) => s + b.valor, 0)
  const maior = Math.max(1, ...barras.map((b) => b.valor))
  const cortar = limite !== undefined && !todas && barras.length > limite
  // A escolhida nunca some atrás do "ver todas".
  const visiveis = cortar
    ? barras.filter((b, i) => i < limite! || b.chave === selecionada)
    : barras

  return (
    <section className="cartao p-4">
      <h3 className="mb-2 text-sm font-semibold text-slate-800">{titulo}</h3>
      {barras.length === 0 ? (
        <p className="py-4 text-center text-sm text-slate-400">Nada com esses filtros.</p>
      ) : (
        <ul className="space-y-0.5">
          {visiveis.map((b) => {
            const ativa = selecionada === b.chave
            const apagada = selecionada !== undefined && !ativa
            const cor = estado ? (COR_ESTADO[b.chave] ?? 'bg-marinho') : 'bg-marinho'
            return (
              <li key={b.chave}>
                <button
                  onClick={() => onEscolher(b.chave)}
                  aria-pressed={ativa}
                  title={`${b.rotulo}: ${b.valor.toLocaleString('pt-BR')} (${pct(b.valor, soma)})`}
                  className={`grid min-h-[28px] w-full grid-cols-[minmax(0,9rem)_1fr_auto] items-center gap-2 rounded px-1 text-left text-xs transition hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-marinho sm:grid-cols-[minmax(0,11rem)_1fr_auto] ${
                    ativa ? 'bg-marinho-50' : ''
                  }`}
                >
                  <span className={`truncate ${ativa ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                    {b.rotulo}
                  </span>
                  <span className="h-3.5">
                    <span
                      className={`block h-full rounded-r ${cor} ${apagada ? 'opacity-25' : ''}`}
                      style={{ width: `${Math.max(2, (b.valor / maior) * 100)}%` }}
                    />
                  </span>
                  <span className="w-12 text-right tabular-nums text-slate-700">{b.valor.toLocaleString('pt-BR')}</span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
      {limite !== undefined && barras.length > limite && (
        <button onClick={() => setTodas(!todas)} className="mt-2 text-xs font-semibold text-marinho underline">
          {todas ? 'Mostrar só as 10 maiores' : `Ver todas (${barras.length})`}
        </button>
      )}
    </section>
  )
}

/// Colunas por mês. Uma série só, então sem legenda: o título diz o que é. O valor
/// vai escrito no maior mês e no último; os demais aparecem ao passar o dedo ou o
/// mouse, e todos estão na tabela.
function GraficoMeses({
  linhas,
  selecao,
  obras,
  hoje,
  selecionado,
  onEscolher,
}: {
  linhas: LinhaPainel[]
  selecao: Selecao
  obras: Map<string, string>
  /// aaaa-mm-dd, do servidor
  hoje: string
  selecionado: string | undefined
  onEscolher: (mes: string) => void
}) {
  const [tabela, setTabela] = useState(false)
  const [foco, setFoco] = useState<string | null>(null)

  const serie = useMemo(() => {
    const contagem = new Map<string, number>()
    for (const l of linhas) {
      if (!l.data || !atende(l, selecao, obras, 'mes')) continue
      const m = l.data.slice(0, 7)
      contagem.set(m, (contagem.get(m) ?? 0) + 1)
    }
    const chaves = [...contagem.keys()].sort()
    if (chaves.length === 0) return { seq: [], fora: 0 }
    // No máximo os 36 meses que terminam no mês atual. Uma data digitada errada
    // (2062, 1926) esticaria o eixo em centenas de colunas vazias; o que fica de
    // fora é contado abaixo do gráfico e continua na tabela de registros.
    const indice = (anoMes: string) => {
      const [a, m] = anoMes.split('-').map(Number) as [number, number]
      return a * 12 + m - 1
    }
    const fim = Math.min(indice(chaves.at(-1)!), indice(hoje.slice(0, 7)))
    const inicio = Math.max(indice(chaves[0]!), fim - 35)
    // Todos os meses do intervalo, inclusive os vazios: um mês sem registro é
    // informação, e pular a coluna encurtaria o tempo.
    const seq: { mes: string; valor: number }[] = []
    for (let i = inicio; i <= fim; i++) {
      const chave = `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`
      seq.push({ mes: chave, valor: contagem.get(chave) ?? 0 })
    }
    let fora = 0
    for (const [chave, n] of contagem) if (indice(chave) < inicio || indice(chave) > fim) fora += n
    return { seq, fora }
  }, [linhas, selecao, obras, hoje])
  const { seq: meses, fora } = serie

  const maior = Math.max(1, ...meses.map((x) => x.valor))
  const indiceMaior = meses.findIndex((x) => x.valor === maior)
  const ALTURA = 140

  return (
    <section className="cartao p-4">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-800">Registros por mês</h3>
        <button onClick={() => setTabela(!tabela)} className="text-xs font-semibold text-marinho underline">
          {tabela ? 'Ver gráfico' : 'Ver tabela'}
        </button>
      </div>
      {meses.length === 0 ? (
        <p className="py-4 text-center text-sm text-slate-400">Nada com esses filtros.</p>
      ) : tabela ? (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-slate-500">
              <th className="py-1 font-medium">Mês</th>
              <th className="py-1 text-right font-medium">Registros</th>
            </tr>
          </thead>
          <tbody>
            {meses.map((x) => (
              <tr key={x.mes} className="border-t border-slate-100">
                <td className="py-1 text-slate-700">{rotuloMes(x.mes)}</td>
                <td className="py-1 text-right tabular-nums text-slate-700">{x.valor.toLocaleString('pt-BR')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="flex items-end gap-0.5" style={{ height: ALTURA + 36 }} onMouseLeave={() => setFoco(null)}>
          {meses.map((x, i) => {
            const ativo = selecionado === x.mes
            const apagado = selecionado !== undefined && !ativo
            const rotular = i === indiceMaior || i === meses.length - 1 || foco === x.mes
            // Rótulo do eixo a cada poucos meses quando são muitos, para não encavalar.
            const passo = meses.length > 18 ? 3 : meses.length > 9 ? 2 : 1
            return (
              <button
                key={x.mes}
                onClick={() => onEscolher(x.mes)}
                onMouseEnter={() => setFoco(x.mes)}
                onFocus={() => setFoco(x.mes)}
                onBlur={() => setFoco(null)}
                aria-pressed={ativo}
                aria-label={`${rotuloMes(x.mes)}: ${x.valor} registros`}
                className="group flex h-full min-w-0 flex-1 flex-col items-center justify-end focus-visible:outline focus-visible:outline-2 focus-visible:outline-marinho"
              >
                <span className={`mb-0.5 text-[10px] tabular-nums text-slate-700 ${rotular ? '' : 'invisible'}`}>
                  {x.valor.toLocaleString('pt-BR')}
                </span>
                <span
                  className={`block w-full max-w-[24px] rounded-t bg-marinho ${apagado ? 'opacity-25' : ''} ${
                    foco === x.mes ? 'opacity-80' : ''
                  }`}
                  style={{ height: `${x.valor > 0 ? Math.max(2, (x.valor / maior) * ALTURA) : 0}px` }}
                />
                <span className="mt-1 h-4 w-full border-t border-slate-200 pt-0.5 text-center text-[10px] text-slate-500">
                  {i % passo === 0 ? rotuloMes(x.mes) : ''}
                </span>
              </button>
            )
          })}
        </div>
      )}
      {fora > 0 && (
        <p className="mt-2 text-[11px] text-slate-500">
          {fora.toLocaleString('pt-BR')} {fora === 1 ? 'registro' : 'registros'} com data antes dos últimos 36
          meses ou no futuro {fora === 1 ? 'não aparece' : 'não aparecem'} no gráfico; estão na tabela de registros.
        </p>
      )}
    </section>
  )
}

function TabelaRegistros({ linhas, obras }: { linhas: LinhaPainel[]; obras: Map<string, string> }) {
  const [quantos, setQuantos] = useState(20)
  const ordenadas = useMemo(
    () => [...linhas].sort((a, b) => (b.data ?? '').localeCompare(a.data ?? '') || b.codigo.localeCompare(a.codigo)),
    [linhas],
  )
  return (
    <section className="cartao overflow-hidden">
      <h3 className="px-4 pt-4 text-sm font-semibold text-slate-800">Registros ({linhas.length.toLocaleString('pt-BR')})</h3>
      <div className="overflow-x-auto">
        <table className="mt-2 w-full min-w-[640px] text-xs">
          <thead>
            <tr className="border-b border-slate-200 text-left text-slate-500">
              <th className="px-4 py-2 font-medium">Data</th>
              <th className="px-2 py-2 font-medium">Tipo</th>
              <th className="px-2 py-2 font-medium">Plano de ação</th>
              <th className="px-2 py-2 font-medium">Local</th>
              <th className="px-2 py-2 font-medium">Obra</th>
              <th className="px-2 py-2 font-medium">Gerente</th>
            </tr>
          </thead>
          <tbody>
            {ordenadas.slice(0, quantos).map((l) => (
              <tr key={l.id} className="border-b border-slate-100 align-top">
                <td className="whitespace-nowrap px-4 py-1.5 tabular-nums text-slate-700">
                  <Link to={`/inspecao/${l.id}/ver`} className="text-marinho underline">
                    {l.data ? new Date(`${l.data}T00:00:00`).toLocaleDateString('pt-BR') : '—'}
                  </Link>
                </td>
                <td className="px-2 py-1.5 text-slate-700">{l.tipo ?? '—'}</td>
                <td className="px-2 py-1.5">
                  <span className="inline-flex items-center gap-1 text-slate-700">
                    <span className={`inline-block h-2 w-2 rounded-full ${COR_ESTADO[l.plano] ?? 'bg-slate-300'}`} />
                    {l.plano}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-slate-700">{l.titulo ?? '—'}</td>
                <td className="px-2 py-1.5 text-slate-700">{obras.get(l.obraId) ?? '—'}</td>
                <td className="px-2 py-1.5 text-slate-700">{l.gerente ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {ordenadas.length > quantos && (
        <button
          onClick={() => setQuantos(quantos + 50)}
          className="m-3 text-xs font-semibold text-marinho underline"
        >
          Mostrar mais {Math.min(50, ordenadas.length - quantos)}
        </button>
      )}
    </section>
  )
}
